import CloseOutlined from '@ant-design/icons/CloseOutlined'
import EditOutlined from '@ant-design/icons/EditOutlined'
import FullscreenOutlined from '@ant-design/icons/FullscreenOutlined'
import LinkOutlined from '@ant-design/icons/LinkOutlined'
import PushpinFilled from '@ant-design/icons/PushpinFilled'
import PushpinOutlined from '@ant-design/icons/PushpinOutlined'
import SettingOutlined from '@ant-design/icons/SettingOutlined'
import { Button, Card, ConfigProvider, Input, InputRef, Typography } from 'antd'
import {
  copyTextToClipboard,
  DEFAULT_EXPLORATION_NAME,
  DrilldownDataContext,
  useDrilldownPanelData,
} from 'features/drilldown'
import { ExplorerDrawerConfigPanel } from 'features/drilldown/components/config/ExplorerDrawerConfigPanel'
import { DRILLDOWN_PANEL_CONFIGS } from 'features/drilldown/components/panelConfigs/PanelConfigs'
import { useGeckoNotifications } from 'features/notifications'
import React, {
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { DrilldownPanelType, GenericPanelData } from 'types'

import { FullscreenModal } from './FullscreenModal'
import { PanelContextProvider } from './PanelContext'

const { Text } = Typography

const DEFAULT_CARD_PADDING = 24
const DEFAULT_ICON_COLOR = CantileverTheme.colors.slate['400']

export type DrilldownPanelProps = {
  panelType: DrilldownPanelType
  onClose?: () => void
  icon?: ReactNode
  configPanel?: ReactNode
  panelPadding?: number
  panelId?: string
}

const CARD_STYLE: React.CSSProperties = {
  width: '100%',
}

interface DrilldownPanelTitleProps {
  icon?: ReactNode
}

const HeaderContainer = styled.div`
  justify-content: space-between;
  display: flex;
  flex-direction: row;
  padding-right: 0.1rem;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const DrilldownPanelTitle: FC<DrilldownPanelTitleProps> = ({ icon }) => {
  const { isPresentationModeEnabled } = useContext(DrilldownDataContext)
  const { panelData, setPanelData } = useDrilldownPanelData<GenericPanelData>()
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const inputFieldRef = useRef<InputRef>(null)

  const { isPinned, title } = panelData

  const toggleIsPinned = useCallback(() => {
    setPanelData((prev: GenericPanelData) => {
      return {
        ...prev,
        isPinned: !prev.isPinned,
      }
    })
  }, [setPanelData])

  const setTitle = useCallback(
    (newTitle: string) => {
      setPanelData((prev: GenericPanelData) => ({ ...prev, title: newTitle }))
    },
    [setPanelData]
  )

  useEffect(() => {
    if (isEditing) {
      inputFieldRef.current?.focus()
    } else {
      inputFieldRef.current?.blur()
    }
  }, [isEditing])

  return (
    <HeaderContainer>
      <TitleContainer>
        {!!icon && <span style={{ color: DEFAULT_ICON_COLOR }}>{icon}</span>}
        {!isEditing && <Text>{title}</Text>}
        <Input
          style={{ display: isEditing ? 'inline' : 'none' }}
          ref={inputFieldRef}
          value={title}
          onBlur={() => setIsEditing(false)}
          onChange={(e) => setTitle(e.target.value)}
          onPressEnter={() => setIsEditing(false)}
        />
        <ConfigProvider
          theme={{
            components: {
              Button: {
                colorText: CantileverTheme.colors.grayscale[400],
              },
            },
          }}>
          {!isPresentationModeEnabled && (
            <Button
              shape='circle'
              size='small'
              type='text'
              onClick={() => setIsEditing(true)}>
              <EditOutlined style={{ opacity: 0.4 }} />
            </Button>
          )}
        </ConfigProvider>
      </TitleContainer>
      {!isPresentationModeEnabled && (
        <Button
          shape='circle'
          size='small'
          type='text'
          onClick={toggleIsPinned}>
          {isPinned ? (
            <PushpinFilled
              style={{
                color: CantileverTheme.colors.highVis,
                opacity: 1,
              }}
            />
          ) : (
            <PushpinOutlined
              style={{
                opacity: 0.6,
              }}
            />
          )}
        </Button>
      )}
    </HeaderContainer>
  )
}

export const AbstractDrilldownPanel: FC<
  DrilldownPanelProps &
    DrilldownPanelTitleProps & {
      configPanel?: ReactNode
      children: ReactNode
    }
> = ({ children, onClose, icon, configPanel, panelPadding, panelId }) => {
  const { isPresentationModeEnabled, explorationTitle } =
    useContext(DrilldownDataContext)
  const { panelData, setPanelData, focusPanelId } =
    useDrilldownPanelData<GenericPanelData>()
  const notificationContext = useGeckoNotifications()

  const cardRef = useRef<HTMLDivElement>(null)
  const [isFullscreenModalOpen, setIsFullscreenModalOpen] = useState(false)
  const [shouldRenderFullscreenContent, setShouldRenderFullscreenContent] =
    useState(false)
  const hasBeenManuallyClosed = useRef(false)

  useEffect(() => {
    setTimeout(
      () => cardRef.current?.scrollIntoView({ behavior: 'smooth' }),
      50
    )
  }, [])

  // Auto-opens full screen modal (if the panel is mentioned in the URL)
  useEffect(() => {
    if (
      focusPanelId &&
      focusPanelId === panelId &&
      !isFullscreenModalOpen &&
      !hasBeenManuallyClosed.current
    ) {
      setIsFullscreenModalOpen(true)
    }
  }, [focusPanelId, panelId, isFullscreenModalOpen])

  useEffect(() => {
    hasBeenManuallyClosed.current = false
  }, [focusPanelId])

  // Rerender the panel content after the full screen modal animation.
  // This is to handle an edge case where the site view render is smaller than the modal,
  // seemingly because it tries to render the panel content before the modal is fully open.
  useEffect(() => {
    if (isFullscreenModalOpen) {
      const timer = setTimeout(() => {
        setShouldRenderFullscreenContent(true)
      }, 800)
      return () => clearTimeout(timer)
    }
  }, [isFullscreenModalOpen])

  const handleOpenInFullscreen = useCallback(() => {
    setIsFullscreenModalOpen(true)
  }, [])

  const handleCloseFullscreen = useCallback(() => {
    setIsFullscreenModalOpen(false)
    setShouldRenderFullscreenContent(false)
    hasBeenManuallyClosed.current = true
  }, [setIsFullscreenModalOpen])

  const handleConfigToggle = () => {
    setPanelData((prev) => ({
      ...prev,
      isConfigOpen: !prev.isConfigOpen,
    }))
  }

  const handleConfigClose = () => {
    setPanelData((prev) => ({
      ...prev,
      isConfigOpen: false,
    }))
  }

  const handleCopyFullscreenLink = useCallback(async () => {
    if (!panelId) return

    try {
      // Includes exploration id and any other params
      const currentUrl = new URL(window.location.href)

      currentUrl.searchParams.set('focus', panelId)

      await copyTextToClipboard(currentUrl.toString(), () => {
        notificationContext.successNotification(
          'Fullscreen link copied to clipboard'
        )
      })
    } catch (error) {
      console.error('Failed to copy link:', error)
      notificationContext.errorNotification('Failed to copy link to clipboard')
    }
  }, [panelId, notificationContext])

  return (
    <PanelContextProvider isFullscreen={isFullscreenModalOpen}>
      <ConfigProvider
        theme={{
          token: {
            lineWidth: 0.5,
          },
          components: {
            Card: {
              bodyPadding: panelPadding ?? DEFAULT_CARD_PADDING,
            },
          },
        }}>
        <Card
          style={CARD_STYLE}
          variant='borderless'
          ref={cardRef}
          title={<DrilldownPanelTitle icon={icon} />}
          extra={
            <>
              {!!configPanel && !isPresentationModeEnabled && (
                <Button
                  type={panelData.isConfigOpen ? 'primary' : 'text'}
                  icon={<SettingOutlined />}
                  onClick={handleConfigToggle}
                />
              )}
              {panelId &&
                // The title here is a proxy for whether the exploration is saved as the link doesn't make sense if not saved
                explorationTitle &&
                explorationTitle !== DEFAULT_EXPLORATION_NAME && (
                  <Button
                    size='small'
                    type='text'
                    icon={<LinkOutlined />}
                    onClick={handleCopyFullscreenLink}
                    title='Copy fullscreen link'
                  />
                )}
              <Button
                type={'text'}
                icon={<FullscreenOutlined />}
                onClick={handleOpenInFullscreen}></Button>
              {!!onClose && !isPresentationModeEnabled && (
                <Button
                  type='text'
                  icon={<CloseOutlined />}
                  onClick={onClose}
                />
              )}
            </>
          }>
          {children}
        </Card>
      </ConfigProvider>

      {!!configPanel && (
        <ExplorerDrawerConfigPanel
          open={!!panelData.isConfigOpen}
          onClose={handleConfigClose}
          title='Panel Configuration'>
          {configPanel}
        </ExplorerDrawerConfigPanel>
      )}

      <FullscreenModal
        open={isFullscreenModalOpen}
        onClose={handleCloseFullscreen}
        title={panelData.title}
        icon={icon}>
        {shouldRenderFullscreenContent ? children : null}
      </FullscreenModal>
    </PanelContextProvider>
  )
}

const RawDrilldownPanel: FC<
  DrilldownPanelProps & { panelType: DrilldownPanelType }
> = ({ panelType, ...props }) => {
  const panelSpec = DRILLDOWN_PANEL_CONFIGS[panelType]

  if (!panelSpec || !panelSpec.render) {
    throw new Error(`Unknown panel type: ${panelType}`)
  }

  const childProps = useMemo(
    () => ({ ...props, icon: panelSpec.icon, panelType }),
    [panelSpec.icon, props, panelType]
  )
  return panelSpec.render(childProps)
}

export const DrilldownPanel = React.memo(RawDrilldownPanel)
